import React, { memo, useEffect, useRef } from 'react';
import { useIntl, defineMessages, FormattedMessage } from 'react-intl';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import Skeleton from '@atlaskit/skeleton';
import MarketplaceIcon from '@atlaskit/icon/core/marketplace';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';

import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import type { useWebItemLocationWebItemType as WebItem } from '@confluence/web-item-location/entry-points/useWebItemLocation.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import type { ForgeUIGlobalPageExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { LoadableAfterPaint, LoadableLazy } from '@confluence/loadable';
import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';

import { AppItems } from './AppItems.tsx';

const AsyncDiscoverSection = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-apps-menu-recommeded" */
				'./DiscoverSection.tsx'
			)
		).DiscoverSection,
});

const AsyncDiscoverSectionPostOffice = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-apps-menu-post-office-recommended" */
				'./DiscoverSectionPostOffice.tsx'
			)
		).DiscoverSectionPostOffice,
});

const DISCOVER_URL = `${CONTEXT_PATH}/marketplace`;

type WrappedAppsMenuContentProps = {
	loading?: boolean;
	errors?: Error[];
	webItems?: WebItem[];
	extensions?: ForgeUIGlobalPageExtensionType[];
};

const errorMessages = defineMessages({
	errorTitle: {
		id: 'side-navigation.global.apps.error-title',
		defaultMessage: 'We couldn’t load apps',
		description: 'Title for the error flag when fetching apps fails',
	},
	errorDescription: {
		id: 'side-navigation.global.apps.error-description',
		defaultMessage: 'Refresh and try again.',
		description: 'Description for the error flag when fetching apps fails',
	},
});

export const AppsMenuContent: React.ComponentType<Omit<WrappedAppsMenuContentProps, 'flags'>> =
	withFlags(
		memo(
			({
				loading = true,
				errors = [],
				webItems = [],
				extensions = [],
				flags,
			}: WrappedAppsMenuContentProps & WithFlagsProps) => {
				const { formatMessage } = useIntl();
				const { createAnalyticsEvent } = useAnalyticsEvents();
				const hasShownErrorFlag = useRef(false);

				useEffect(() => {
					if (errors.length > 0 && !hasShownErrorFlag.current) {
						hasShownErrorFlag.current = true;
						void flags.showFlag({
							title: formatMessage(errorMessages.errorTitle),
							description: formatMessage(errorMessages.errorDescription),
							type: 'error',
							close: 'auto',
						});
					} else if (errors.length === 0) {
						hasShownErrorFlag.current = false;
					}
				}, [errors, flags, formatMessage]);

				const fireAppsNavigationItemClicked = (itemId?: string | null) => () => {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'navigationItem',
							actionSubjectId: 'appsNavigationMenuItem',
							source: 'globalNavigation',
							attributes: {
								navigationLayer: 'global',
								navigationContainer: 'sidebar',
								navVersion: '4',
								componentType: 'customItem',
								itemType: 'app',
								itemId,
								section: 'yourApps',
							},
						},
					}).fire();
				};

				const pathname = useRouteUrl({
					selector: (routeUrl: string | undefined) => routeUrl?.split('?')[0] ?? '',
				});

				const hasApps = webItems.length > 0 || extensions.length > 0;
				const displayApps = hasApps && !loading;

				return (
					<>
						<MenuSection>
							{displayApps && (
								<MenuSectionHeading>
									<FormattedMessage {...i18n.yourApps} />
								</MenuSectionHeading>
							)}
							<MenuList>
								{displayApps && (
									<AppItems
										webItems={webItems}
										extensions={extensions}
										pathname={pathname}
										fireAppsNavigationItemClicked={fireAppsNavigationItemClicked}
									/>
								)}
								{loading && <LoadingSkeletons />}
							</MenuList>
						</MenuSection>
						{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
						{fg('confluence_nav_4_apps_menu_recommend') &&
							(expVal('enable_confluence_sidenav_2p_app_recommendations', 'isEnabled', false) ? (
								<AsyncDiscoverSectionPostOffice />
							) : (
								<AsyncDiscoverSection />
							))}
						{/* environment-aware enablement for Apps discovery via feature encoding */}
						{isFeatureEnabled('adminhub', 'adminhub-apps-discovery') && (
							<MenuSection>
								<MenuList>
									<LinkMenuItem
										isSelected={pathname.includes(DISCOVER_URL)}
										href={DISCOVER_URL}
										elemBefore={<MarketplaceIcon label="" color="currentColor" />}
										onClick={fireAppsNavigationItemClicked('exploreApps')}
									>
										{loading || hasApps ? (
											<FormattedMessage {...i18n.exploreMoreApps} />
										) : (
											<FormattedMessage {...i18n.exploreApps} />
										)}
									</LinkMenuItem>
								</MenuList>
							</MenuSection>
						)}
					</>
				);
			},
		),
	);

const LoadingSkeletons = () => {
	return (
		<Box paddingBlockStart="space.100" testId="loading">
			<Stack space="space.100">
				<Skeleton width="100%" height="20px" />
				<Skeleton width="100%" height="20px" />
				<Skeleton width="100%" height="20px" />
			</Stack>
		</Box>
	);
};

const i18n = defineMessages({
	yourApps: {
		id: 'side-navigation.global.apps.your-apps',
		defaultMessage: 'Your apps',
		description: 'Label for the section of the global navigation that displays apps',
	},
	exploreApps: {
		id: 'side-navigation.global.apps.explore-apps',
		defaultMessage: 'Explore apps',
		description:
			'Label for the link to find apps. Appears in global navigation when there are no apps installed yet.',
	},
	exploreMoreApps: {
		id: 'side-navigation.global.apps.explore-more-apps',
		defaultMessage: 'Explore more apps',
		description:
			'Label for the link to find apps. Appears in global navigation, underneath already installed apps.',
	},
});
